/**
 * Obstruction Form Wizard Logic Orchestrator
 *
 * Mirrors `useTaskWizardLogic`: composes form + navigation + submission
 * into a single hook the container consumes. The country-aware location field +
 * its GPS road-detection are wired locally in the basic-info step
 * (`useObstructionRoadDetection`), not here. The anchor point is read through
 * for unsaved-changes detection and edit-mode seeding.
 */
import { useEffect, useMemo } from 'react';

import { useGeoMarkupStore } from '@/store';
import type { Coordinates } from '@/types';
import type { Obstruction } from '@/types/obstructions';

import {
  getDefaultObstructionFormValues,
  obstructionToFormValues,
  useObstructionForm,
} from './useObstructionForm';
import { useObstructionWizardNavigation } from './useObstructionWizardNavigation';
import { useObstructionWizardSubmission } from './useObstructionWizardSubmission';

interface UseObstructionFormWizardLogicProps {
  editingObstruction: Obstruction | null;
}

/** Public surface consumed by the wizard container. */
export interface UseObstructionFormWizardLogicReturn {
  form: ReturnType<typeof useObstructionForm>['form'];
  // Navigation
  handleClose: () => void;
  forceClose: () => void;
  handleStepClick: (step: number) => void;
  // Submission
  isSubmitting: boolean;
  handleSubmit: () => Promise<void>;
  /** Delete an already-persisted attachment (edit mode only). */
  handleDeleteExistingAttachment: (attachmentId: string) => Promise<void>;
  // Anchor (read-through for steps)
  anchorPoint: Coordinates | null;
  setAnchorPoint: (point: Coordinates | null) => void;
}

export function useObstructionFormWizardLogic({
  editingObstruction,
}: UseObstructionFormWizardLogicProps): UseObstructionFormWizardLogicReturn {
  // Initial values: defaults for create, mapped obstruction for edit. We
  // only rebuild when the obstruction id flips — selectedObstruction's
  // reference is replaced on every slice update.
  const initialValues = useMemo(
    () =>
      editingObstruction
        ? obstructionToFormValues(editingObstruction)
        : getDefaultObstructionFormValues(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingObstruction?.id]
  );

  const { form, goToStep } = useObstructionForm({
    initialValues,
  });

  const anchorPoint = useGeoMarkupStore(
    (state) => state.obstructions.formAnchorPoint
  );
  const setAnchorPoint = useGeoMarkupStore(
    (state) => state.obstructions.setFormAnchorPoint
  );

  // Seed anchor point on mount (edit mode loads from obstruction).
  useEffect(() => {
    if (editingObstruction) {
      setAnchorPoint(editingObstruction.anchorPoint);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingObstruction?.id]);

  const navigation = useObstructionWizardNavigation({
    form,
    editingObstruction,
    goToStep,
    anchorPoint,
  });

  const submission = useObstructionWizardSubmission({
    form,
    editingObstruction,
    anchorPoint,
    forceClose: navigation.forceClose,
    goToStep,
  });

  return {
    form,
    handleClose: navigation.handleClose,
    forceClose: navigation.forceClose,
    handleStepClick: navigation.handleStepClick,
    isSubmitting: submission.isSubmitting,
    handleSubmit: submission.handleSubmit,
    handleDeleteExistingAttachment: submission.handleDeleteExistingAttachment,
    anchorPoint,
    setAnchorPoint,
  };
}
